import { orderService } from "@/services/api/orderService";
import Utils from "@/utils/utils";
import {applyDateFormat} from "@/utils/dateUtils";
import {Order, Workflow, Mode} from "@/constants/constants";

export default {
  methods: {
    async doCheckIfOrdersAreCurrentlyPossible(orderType = Order.numericType.DINE_IN) {
      let hasTimeSlotForDay = false;

      const result = await orderService.getTimeSlots();
      const availabilities = result.availabilityList;
      if (!availabilities) {
        this.makeToast('danger', this.$t('notification.noTimeslotsAvailable'));
        this.$navigateToErrorLanding();
        return;
      }

      const mapDayWithTimeslots = new Map(
        availabilities.map(obj => [new Date(obj.date).toLocaleDateString(), obj.timeslots])
      );

      const currentDate = new Date();
      if (mapDayWithTimeslots.size > 0) {
        const timeslots = mapDayWithTimeslots.get(currentDate.toLocaleDateString());
        if (timeslots?.length > 0) {
          hasTimeSlotForDay = timeslots.some(timeslot => {
            const dateFrom = Utils.parseTimeHHMMSS(timeslot.fromtime);
            const dateTo = Utils.parseTimeHHMMSS(timeslot.totime);
            return currentDate >= dateFrom && currentDate <= dateTo;
          });
        }
      }

      if (hasTimeSlotForDay) {
        const resultOcc = await orderService.getTimeSlotOccupancies(orderType, currentDate);
        const occupancies = resultOcc?.timeSlots;
        if (!occupancies) {
          this.makeToast('danger', this.$t('notification.noTimeslotsAvailable'));
          this.$navigateToErrorLanding();
          return;
        }

        this.ordersPossible = occupancies.some(timeslot => {
          const dateFrom = Utils.parseTimeHHMMSS(timeslot.fromTime);
          const dateTo = Utils.parseTimeHHMMSS(timeslot.toTime);
          return currentDate >= dateFrom && currentDate <= dateTo && timeslot.slots > 0 && timeslot.usable > 0;
        });
      }
    },

    async doImmediateOrder(shopCode) {
      const result = await orderService.immediateOrder(shopCode);
      if (result.exception) {
        this.makeToast('danger', this.$t('notification.failedGeneric'));
        this.$navigateToErrorLanding();
        return false;
      }

      this.shopStore.setShop(result.shop.id, result.shop.custom1, result.shop.custom2, result.shop.name, result.shop.image, result.shop.custom6);
      return true;
    },
    async startImmediateOrderFlow(shopCode, options = {}) {
      const {
        orderType = Order.type.TAKEAWAY,
        numericOrderType = Order.numericType.TAKEAWAY,
        navigate = () => this.$navigateToOrderMenuPlanner(),
      } = options;

      this.appStore.setWorkflow(Workflow.ORDER);
      this.appStore.setMode(Mode.IMMEDIATE);

      const immediateOrderStarted = await this.doImmediateOrder(shopCode);
      if (!immediateOrderStarted) {
        return false;
      }

      await this.doCheckIfOrdersAreCurrentlyPossible(numericOrderType);
      this.appStore.setCurrentOrderMenuOrderType(orderType);
      await navigate();
      return true;
    },
    async selectTableByQRCode(shopCode, tableCode, closeTransaction) {
      const result = await orderService.selectTableByQRCode(shopCode, tableCode, closeTransaction);
      if (result.exception) {
        this.makeToast('danger', this.$t('notification.failedGeneric'));
        this.$navigateToErrorLanding();
        return false;
      }

      this.shopStore.setShop(result.shop.id, result.shop.custom1, result.shop.custom2, result.shop.name, result.shop.image, result.shop.custom6);
      return true;
    },

    async confirmOrder(guid, tableId, shopId) {
      if (!guid || !shopId) {
        this.makeToast('danger', this.$t('notification.confirmOrderError'));
        return false;
      }
      try {
        const result = await orderService.modifyOrder(guid, tableId, shopId, true);
        if (result.exception || result.errorCode !== 0) {
          const hasLowBalanceError = Number(result?.errorCode) === 95 || Number(result?.exception?.code) === 95;
          const messageKey = hasLowBalanceError
            ? 'notification.confirmOrderLowBalanceError'
            : 'notification.confirmOrderError';
          this.makeToast('danger', this.$t(messageKey));
          return false;
        } else {
          this.makeToast('success', this.$t('notification.confirmOrderSuccessful'));
          return true;
        }
      } catch (error) {
        this.makeToast('danger', this.$t('notification.confirmOrderError'));
        return false;
      }
    },

    async cancelOrder(shopStore, guid) {
      if (!shopStore || !shopStore.wondId || !guid) {
        this.makeToast('danger', this.$t('notification.cancelOrderError'));
        return false;
      }
      try {
        const result = await orderService.cancelOrder(shopStore.wondId, guid, false);
        if (result.exception) {
          this.makeToast('danger', this.$t('notification.cancelOrderError'));
          return false;
        } else {
          this.makeToast('success', this.$t('notification.cancelOrderSuccessful'));
          return true;
        }
      } catch (error) {
        this.makeToast('danger', this.$t('notification.cancelOrderError'));
        return false;
      }
    },

    async retrieveOrders(dateFrom, dateTo, shopId = undefined, options = {}) {
      this.isLoading = true;
      const result = await orderService.getOrders(dateFrom, dateTo, shopId, options);
      this.isLoading = false;

      if (result.errorCode < 0) {
        this.makeToast('danger', result.errorMsg);
        return;
      }
      applyDateFormat(result);

      this.recentOrders = result.orders;
      if (result?.basketTile?.table) {
        this.table = {
          id: result.basketTile.table.id ?? null,
          code: result.basketTile.table.code ?? null,
        };
      }
    },

    async getOrderDetails(guid, shopId) {
      if (!guid || !shopId) return null;
      this.loading = true;
      try {
        const result = await orderService.getOrderDetails(guid, shopId);
        this.orderDetails = result.order;
      } catch (e) {
        if (typeof this.handleOrderDetailsError === 'function') {
          this.handleOrderDetailsError(e);
        }
        return null;
      } finally {
        this.loading = false;
      }
    },
  }
};
